import { ReactNode } from 'react';

import { CourseRole } from '../../types';
import useCellAriaLabel, { UseCellAriaLabel } from './useAriaLabel';
import useCellSubtitle, { UseCellSubtitle } from './useCellSubtitle';
import useCellTitle, { UseCellTitle } from './useCellTitle';

interface TitleWithEmailId {
  isEmailIdNeeded: true;
  isCourseRoleNeeded?: false;
  title?: ReactNode | ((name?: string, emailId?: string) => ReactNode);
  subtitle?: ReactNode | ((name?: string, emailId?: string) => ReactNode);
  ariaLabel?: string | ((name?: string, emailId?: string) => string);
}

interface TitleWithCourseRole {
  isEmailIdNeeded?: false;
  isCourseRoleNeeded: true;
  existingCourseRole: CourseRole | undefined;
  title?: ReactNode | ((name?: string, courseRole?: CourseRole) => ReactNode);
  subtitle?: ReactNode | ((name?: string, courseRole?: CourseRole, isRemovedMember?: boolean) => ReactNode);
  ariaLabel?: string | ((name?: string, courseRole?: CourseRole, isRemovedMember?: boolean) => string);
}

interface TitleWithEmailIdAndCourseRole {
  isEmailIdNeeded: true;
  isCourseRoleNeeded: true;
  existingCourseRole: CourseRole | undefined;
  title?: ReactNode | ((name?: string, emailId?: string, courseRole?: CourseRole) => ReactNode);
  subtitle?:
    | ReactNode
    | ((name?: string, emailId?: string, courseRole?: CourseRole, isRemovedMember?: boolean) => ReactNode);
  ariaLabel?:
    | string
    | ((name?: string, emailId?: string, courseRole?: CourseRole, isRemovedMember?: boolean) => string);
}

interface TitleWithoutEmailIdAndCourseRole {
  isEmailIdNeeded?: false;
  isCourseRoleNeeded?: false;
  title?: ReactNode | ((name?: string) => ReactNode);
  subtitle?: ReactNode | ((name?: string) => ReactNode);
  ariaLabel?: string | ((name?: string) => string);
}

export type UseCourseUserCell = (
  | TitleWithEmailId
  | TitleWithCourseRole
  | TitleWithEmailIdAndCourseRole
  | TitleWithoutEmailIdAndCourseRole
) & {
  avatarId: MongoId;
};

const useCourseUserCell = ({
  avatarId,
  isEmailIdNeeded = false,
  isCourseRoleNeeded = false,
  title,
  subtitle,
  ariaLabel,
}: UseCourseUserCell) => {
  const cellTitle = useCellTitle({
    avatarId,
    isEmailIdNeeded,
    isCourseRoleNeeded,
    title,
  } as UseCellTitle);

  const cellSubtitle = useCellSubtitle({
    avatarId,
    isEmailIdNeeded,
    isCourseRoleNeeded,
    subtitle,
  } as UseCellSubtitle);

  const cellAriaLabel = useCellAriaLabel({
    avatarId,
    isEmailIdNeeded,
    isCourseRoleNeeded,
    ariaLabel,
  } as UseCellAriaLabel);

  return { cellTitle, cellSubtitle, cellAriaLabel };
};

export default useCourseUserCell;
